import {
	GalleryEntryCard,
	GalleryQuickVote,
	VoteOption,
} from "../JudgingInterfaces";
import styled, { css, useTheme, keyframes } from "styled-components";
import { useState, useEffect } from "react";
import {
	getContrastingTextColor,
	voteButtonColor,
} from "../../../Judging/GalleryCard";
import SlotCounter from "react-slot-counter";
import {
	getRoundBehavior,
	hasSubmittedVote,
	IN_OUT_ROUND_NAME,
	isAbstainedVote,
	isScoreRound,
	MULTIVARIABLE_ROUND_NAME,
	ONE_TO_TEN_ROUND_NAME,
} from "../judgingRoundUtils";

const voteBoxBgAnimation = (background: string) => keyframes`
    from {
        background: transparent;
    }

    to {
        background: ${background};
    }
`;

const StyledVoteBox = styled.div<{
	background?: string;
	large?: boolean;
	length: number;
	hideCount?: boolean;
}>`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	width: 60px;
	height: 60px;
	padding: 0.5rem 1rem;
	gap: 0.5rem;
	background: transparent;

	span[aria-hidden="true"] {
		font-size: 1rem !important;
		font-weight: 600;
	}

	& > * {
		font-size: 10px;
		line-height: 10px;
	}

	.score {
		/* font-size: ${({ theme }) => theme.xSmallSize};
    line-height: ${({ theme }) => theme.xSmallSize}; */
		font-size: 24px;
		line-height: 24px;
		font-weight: 700;
	}

	/* animate bg color */
	${(p) =>
		p.background
			? css`
					font-weight: ${p.theme.fontSemiBold};
					animation: ${voteBoxBgAnimation(p.background)} 750ms ease forwards;
				`
			: `
        border: 1px solid ${p.theme.colorBorderLight};
    `}

	${(p) =>
		p.background &&
		css`
			color: ${getContrastingTextColor(p.background, p.theme)};
		`}

     ${(p) =>
		p.length >= 5 &&
		!p.hideCount &&
		css`
			width: 80px;
		`}

    ${(p) =>
		p.length >= 6 &&
		!p.hideCount &&
		css`
			width: 100px;
		`}

	${(p) =>
		p.large &&
		!p.hideCount &&
		css`
			width: 80px;
			height: 80px;
			.score {
				font-size: 2rem;
				line-height: 2rem;
				font-weight: ${p.theme.fontSemiBold};
			}

			.award {
				font-size: 0.75rem;
				line-height: 0.75rem;
			}

			${p.length >= 5 &&
			css`
				width: 100px;
			`}

			${p.length >= 6 &&
			css`
				width: 135px;
			`}
		`}

  ${(p) =>
		p.hideCount &&
		css`
			box-sizing: border-box;
			display: inline-grid;
			grid-template: 1fr / 1fr;
			justify-items: center;
			align-items: center;
			width: auto;
			height: ${p.large ? "80px" : "60px"};
			padding: 0.5rem 1rem;
			gap: 0;

			.badge-sizer,
			.award {
				grid-area: 1 / 1;
				font-size: 1rem;
				line-height: 1;
				font-weight: ${p.theme.fontSemiBold};
				white-space: nowrap;
			}

			.badge-sizer {
				visibility: hidden;
				pointer-events: none;
			}

			.award {
				text-align: center;
			}
		`}
`;

export function initializeGroups(
	roundType: string,
	roundBehaviorType: string | undefined,
	voteOptions: VoteOption[],
	awardOnly: boolean,
	votedOnly: boolean,
	includeAbstain: boolean,
	includeUnvoted?: boolean,
	scoreAndAbstainOnly?: boolean,
): AwardGroup[] {
	let groups: AwardGroup[] = [];
	if (votedOnly) {
		groups.push({ name: "Voted", count: 0, value: 1, multiplier: 1 });
		if (includeAbstain) {
			groups.push({ name: "Abstain", count: 0, multiplier: 0 });
		}
	} else {
		groups = [
			...voteOptions
				.filter((x) =>
					awardOnly
						? x.isAward
						: x.value || x.value === 0
							? true
							: includeAbstain,
				)
				.map((option) => ({
					name: option.name,
					count: 0,
					value: option.value,
					multiplier: getRoundMultiplier(roundType, roundBehaviorType),
				})),
		];
	}

	if (scoreAndAbstainOnly) {
		groups = groups.filter((x) => x.name === "Abstain");
	}

	if (includeUnvoted) {
		groups.push({ name: "Unvoted", count: 0, multiplier: 0 });
	}

	return groups;
}

export function getRoundMultiplier(
	roundType: string,
	roundBehaviorType?: string,
) {
	switch (getRoundBehavior(roundType, roundBehaviorType)) {
		case IN_OUT_ROUND_NAME:
			return 50;
		case ONE_TO_TEN_ROUND_NAME:
		case MULTIVARIABLE_ROUND_NAME:
			return 10;
		default:
			return 1;
	}
}

export function calculateEntryAwardIndicator(
	roundEntry: GalleryEntryCard,
	roundType: string,
	roundBehaviorType: string | undefined,
	voteOptions: VoteOption[],
	awardOnly: boolean,
	votedOnly: boolean,
): string | undefined {
	// if not every vote has been cast
	// return "Unvoted"
	if (!roundEntry.votes.every((vote) => hasSubmittedVote(vote))) {
		return "Unvoted";
	}

	if (votedOnly) {
		return "Voted";
	}

	// if the entry has already been awarded, return award
	if (roundEntry.awardLevel) {
		return roundEntry.awardLevel;
	}

	// return aggregate score if round type is 1-10
	if (
		isScoreRound(roundType, roundBehaviorType) &&
		roundEntry.score !== undefined
	) {
		return String(roundEntry.score);
	}

	const roundMultiplier = getRoundMultiplier(roundType, roundBehaviorType);

	return voteOptions
		.filter((x) => (awardOnly ? x.isAward : true))
		.find((x) =>
			x.value || x.value === 0
				? roundEntry.score! >= x.value * roundMultiplier
				: true,
		)?.name;
}

export function calculateEntryResultIndicator(
	roundEntry: GalleryEntryCard,
	roundType: string,
	roundBehaviorType: string | undefined,
	voteOptions: VoteOption[],
): string | undefined {
	const awardIndicator = calculateEntryAwardIndicator(
		roundEntry,
		roundType,
		roundBehaviorType,
		voteOptions,
		false,
		false,
	);

	const matchingVoteOption = voteOptions.find(
		(option) =>
			option.name === awardIndicator || option.awardAlias === awardIndicator,
	);

	return matchingVoteOption?.name || awardIndicator;
}

export function calculateEntryAwardGroups({
	roundEntries,
	roundType,
	roundBehaviorType,
	voteOptions,
	awardOnly,
	votedOnly,
	includeAbstain,
	includeUnvoted,
	scoreAndAbstainOnly,
	entryResultMode,
}: AwardSummaryBoxProps): AwardGroup[] {
	let groups: AwardGroup[] = initializeGroups(
		roundType,
		roundBehaviorType,
		voteOptions,
		awardOnly,
		votedOnly,
		includeAbstain,
		includeUnvoted,
		scoreAndAbstainOnly,
	);

	roundEntries?.forEach((entry) => {
		const award =
			entryResultMode && !votedOnly
				? calculateEntryResultIndicator(
						entry,
						roundType,
						roundBehaviorType,
						voteOptions,
					)
				: calculateEntryAwardIndicator(
						entry,
						roundType,
						roundBehaviorType,
						voteOptions,
						awardOnly,
						votedOnly,
					);
		if (award) {
			const group = groups.find((group) => group.name === award);
			if (group) {
				group.count += 1;
			}
		}
	});

	return groups;
}

export function calculateVoteAwardIndicator(
	vote: GalleryQuickVote,
	awardOnly: boolean,
	votedOnly: boolean,
	includeAbstain?: boolean,
) {
	// if vote has not been cast
	// return "Unvoted"
	if (!hasSubmittedVote(vote)) {
		return "Unvoted";
	}

	if (votedOnly) {
		if (includeAbstain && isAbstainedVote(vote)) {
			return vote.voteOption?.name || "Abstain";
		}
		return "Voted";
	}

	if (isAbstainedVote(vote)) {
		return vote.voteOption?.name || "Abstain";
	}

	if (!vote.voteOption) {
		return undefined;
	}

	if (awardOnly) {
		return vote.voteOption.isAward ? vote.voteOption.name : undefined;
	}

	return vote.voteOption.name;
}

export function calculateVoteAwardGroups({
	roundEntry,
	roundType,
	roundBehaviorType,
	voteOptions,
	awardOnly,
	votedOnly,
	includeAbstain,
	includeUnvoted,
	scoreAndAbstainOnly,
}: AwardSummaryBoxProps): AwardGroup[] {
	let groups: AwardGroup[] = initializeGroups(
		roundType,
		roundBehaviorType,
		voteOptions,
		awardOnly,
		votedOnly,
		includeAbstain,
		includeUnvoted,
		scoreAndAbstainOnly,
	);

	roundEntry?.votes?.forEach((vote) => {
		const award = calculateVoteAwardIndicator(
			vote,
			awardOnly,
			votedOnly,
			includeAbstain,
		);
		if (award) {
			const group = groups.find((group) => group.name === award);
			if (group) {
				group.count += 1;
			}
		}
	});

	return groups;
}

export const VoteBox = (props: {
	score: number;
	scoreLength: number;
	award: string;
	background?: string;
	revealResults: boolean;
	large?: boolean;
	hideCount?: boolean;
	/** When set with hideCount, sizes the badge to this label (e.g. longest option). */
	sizeToLabel?: string;
}) => {
	return (
		<StyledVoteBox
			background={props.background}
			large={props.large}
			length={props.scoreLength}
			hideCount={props.hideCount}
		>
			{!props.hideCount && (
				<span className="score">
					{props.revealResults ? (
						<SlotCounter
							value={props.score}
							valueClassName="text-h2Size font-semibold"
							dummyCharacterCount={3}
						/>
					) : (
						"-"
					)}
				</span>
			)}
			{props.hideCount && props.sizeToLabel && (
				<span className="badge-sizer" aria-hidden>
					{props.sizeToLabel}
				</span>
			)}
			<span className="award text-center">{props.award}</span>
		</StyledVoteBox>
	);
};

const AwardSummaryBox = (props: AwardSummaryBoxProps) => {
	const theme = useTheme();
	const [awardSummary, setAwardSummary] = useState<AwardGroup[]>([]);

	useEffect(() => {
		if (props.roundEntry && props.roundEntry.votes) {
			setAwardSummary(calculateVoteAwardGroups(props));
		} else if (props.roundEntries) {
			setAwardSummary(calculateEntryAwardGroups(props));
		} else {
			setAwardSummary([]);
		}
	}, [props]);

	const renderVoteBoxes = () => {
		let voteBoxes = <></>;
		if (props.singleResultOnly && props.roundEntry) {
			const allVotesIn = props.roundEntry.votes.every((vote) =>
				hasSubmittedVote(vote),
			);

			if (!allVotesIn) {
				return voteBoxes;
			}

			const award = props.revealResults
				? calculateEntryResultIndicator(
						props.roundEntry,
						props.roundType,
						props.roundBehaviorType,
						props.voteOptions,
					)
				: "Voted";

			const sizeToLabel = [
				"Voted",
				...props.voteOptions.map((option) => option.name),
			].reduce(
				(longest, label) => (label.length > longest.length ? label : longest),
				award || "",
			);

			return award ? (
				<VoteBox
					key={`${props.roundEntry.id}.${award}`}
					score={0}
					scoreLength={sizeToLabel.length}
					award={award}
					background={voteButtonColor(award, theme)}
					revealResults={false}
					large={props.large}
					hideCount
					sizeToLabel={sizeToLabel}
				/>
			) : (
				voteBoxes
			);
		}

		voteBoxes = (
			<>
				{props.scoreAndAbstainOnly && props.roundEntry && (
					<VoteBox
						key={`${props.roundEntry?.id}.Score`}
						score={props.roundEntry.score!}
						scoreLength={String(props.roundEntry.score!).length}
						award="Score"
						revealResults={props.revealResults}
						large={props.large}
					/>
				)}
				{awardSummary.flatMap((award, i) => (
					<VoteBox
						key={`${props.roundEntry?.id}.${award.name}`}
						score={award.count}
						scoreLength={String(award.count).length}
						award={award.name}
						background={
							props.revealResults &&
							props.roundEntry &&
							(props.roundEntry.awardLevel === award.name ||
								award.name ===
									(props.entryResultMode
										? calculateEntryResultIndicator(
												props.roundEntry,
												props.roundType,
												props.roundBehaviorType,
												props.voteOptions,
											)
										: calculateEntryAwardIndicator(
												props.roundEntry,
												props.roundType,
												props.roundBehaviorType,
												props.voteOptions,
												props.awardOnly,
												props.votedOnly,
											)))
								? voteButtonColor(award.name, theme)
								: undefined
						}
						revealResults={props.revealResults}
						large={props.large}
						hideCount={props.hideCounts}
					/>
				))}
			</>
		);
		return voteBoxes;
	};

	return renderVoteBoxes();
};

export default AwardSummaryBox;

export interface AwardGroup {
	name: string;
	count: number;
	value?: number;
	multiplier: number;
}

export interface AwardSummaryBoxProps {
	roundEntry?: GalleryEntryCard;
	roundEntries?: GalleryEntryCard[];
	roundType: string;
	roundBehaviorType?: string;
	voteOptions: VoteOption[];
	awardOnly: boolean;
	votedOnly: boolean;
	includeAbstain: boolean;
	revealResults: boolean;
	includeUnvoted?: boolean;
	scoreAndAbstainOnly?: boolean;
	entryResultMode?: boolean;
	singleResultOnly?: boolean;
	hideCounts?: boolean;
	large?: boolean;
}
